import React, { useState, useEffect, useRef, useCallback } from "react";

// ─── Animation script ──────────────────────────────────────────────────────
// Each step: cursor moves to `target` ref, `ui` sets the visual state
const SCRIPT = [
  { target: "orderCard",  ui: "idle",    holdMs: 2000 },
  { target: "assessment", ui: "idle",    holdMs: 2200 },
  { target: "breakdown",  ui: "idle",    holdMs: 1400 },
  { target: "button",     ui: "hover",   holdMs: 900  },
  { target: "button",     ui: "click",   holdMs: 280  },
  { target: "button",     ui: "loading", holdMs: 1300 },
  { target: "successMsg", ui: "success", holdMs: 2400 },
  { target: "aiPanel",    ui: "success", holdMs: 1800 },
];

// ─── Cursor SVG ────────────────────────────────────────────────────────────
const FakeCursor = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
    style={{ filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.35))" }}>
    <path d="M3.5 1.5v15l3.8-3.8H13L3.5 1.5z"
      fill="white" stroke="#1a1a1a" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

// ─── Nav items ─────────────────────────────────────────────────────────────
const NAV = ["Dashboard", "Orders", "Refunds", "Store Credit", "Customers", "Analytics"];

export default function StoreCreditDemoComponent() {
  const [ui, setUi]             = useState("idle");
  const [highlight, setHighlight] = useState(null);
  const [cursor, setCursor]     = useState({ x: 0, y: 0 });
  const [ripple, setRipple]     = useState(false);

  const containerRef  = useRef(null);
  const orderCardRef  = useRef(null);
  const assessmentRef = useRef(null);
  const breakdownRef  = useRef(null);
  const buttonRef     = useRef(null);
  const successMsgRef = useRef(null);
  const aiPanelRef    = useRef(null);

  const refs = {
    orderCard:  orderCardRef,
    assessment: assessmentRef,
    breakdown:  breakdownRef,
    button:     buttonRef,
    successMsg: successMsgRef,
    aiPanel:    aiPanelRef,
  };

  const getCenter = useCallback((key) => {
    const el  = refs[key]?.current;
    const box = containerRef.current;
    if (!el || !box) return null;
    const cr = box.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    return {
      x: er.left - cr.left + er.width  * 0.5,
      y: er.top  - cr.top  + er.height * 0.5,
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;
    const run = (idx) => {
      if (cancelled) return;
      const step = SCRIPT[idx % SCRIPT.length];
      if (step.target) {
        const pos = getCenter(step.target);
        if (pos) setCursor(pos);
      }
      setUi(step.ui);
      setHighlight(step.target);
      if (step.ui === "click") {
        setTimeout(() => { if (!cancelled) { setRipple(true);  } }, 80);
        setTimeout(() => { if (!cancelled) { setRipple(false); } }, 460);
      }
      setTimeout(() => run(idx + 1), step.holdMs);
    };
    const t = setTimeout(() => run(0), 700);
    return () => { cancelled = true; clearTimeout(t); };
  }, [getCenter]);

  const isSuccess = ui === "success";
  const isLoading = ui === "loading";
  const isHover   = ui === "hover" || ui === "click";

  const ring = (key) =>
    highlight === key
      ? "ring-2 ring-offset-1 transition-all duration-200"
      : "transition-all duration-200";

  const ringColor = (key) =>
    highlight === key ? { ringColor: "#f97316" } : {};

  // Combined ring style
  const focusStyle = (key) =>
    highlight === key
      ? { outline: "2px solid #f97316", outlineOffset: "2px" }
      : {};

  return (
    <div className="w-full flex justify-center px-4 py-16">
      <div className="w-4/5">

        {/* ── Section header ─────────────────────────────────────────────── */}
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-3"
            style={{ color: "#f97316" }}>
            See it in action
          </span>
          <h2 className="text-3xl font-bold mb-3" style={{ color: "#111827" }}>
            A remedy to the refund pain.
          </h2>
          <p className="text-base max-w-lg mx-auto" style={{ color: "#6b7280" }}>
            Instead of losing $85 to a cash refund, issue store credit instantly.
            Cash refunds walk revenue out the door. Store credit keeps it & brings the customer back.
          </p>
        </div>

        {/* ── Demo shell ─────────────────────────────────────────────────── */}
        <div
          ref={containerRef}
          className="relative rounded-xl overflow-hidden border select-none"
          style={{
            borderColor: "#e0ddd9",
            background: "#f3f2f0",
            boxShadow: "0 20px 60px rgba(0,0,0,0.10), 0 4px 16px rgba(0,0,0,0.06)",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >

          {/* ── Fake cursor ─────────────────────────────────────────────── */}
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              left: cursor.x,
              top:  cursor.y,
              transform: "translate(-2px, -2px)",
              transition: "left 0.65s cubic-bezier(0.25,0.46,0.45,0.94), top 0.65s cubic-bezier(0.25,0.46,0.45,0.94)",
            }}
          >
            {ripple && (
              <span className="absolute pointer-events-none rounded-full"
                style={{
                  width: 32, height: 32, top: -6, left: -6,
                  background: "rgba(249,115,22,0.2)",
                  animation: "pbl-ripple 0.45s ease-out forwards",
                }} />
            )}
            <FakeCursor />
          </div>

          {/* ── Browser chrome ──────────────────────────────────────────── */}
          <div className="flex items-center gap-1.5 px-4 py-2.5 border-b"
            style={{ background: "#eae9e6", borderColor: "#d9d7d3" }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#ff5f57" }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#febc2e" }} />
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#28c840" }} />
            <div className="flex-1 mx-3 rounded px-3 py-1 text-xs flex items-center gap-1.5"
              style={{ background: "#dddbd7", color: "#888580" }}>
              <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              admin.shopify.com · Pabloo · Store Credit Refunds
            </div>
          </div>

          {/* ── App layout ──────────────────────────────────────────────── */}
          <div className="flex" style={{ minHeight: 460 }}>

            {/* ── Sidebar (hidden on mobile) ───────────────────────────── */}
            <div className="hidden md:flex w-40 flex-shrink-0 flex-col border-r py-5 px-0"
              style={{ background: "#ffffff", borderColor: "#e8e6e2" }}>
              {/* Logo */}
              <div className="flex items-center gap-2 px-4 mb-6">
                <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                  style={{ background: "#f97316" }}>
                  <span className="text-white font-bold" style={{ fontSize: 10 }}>P</span>
                </div>
                <span className="text-sm font-semibold" style={{ color: "#111827" }}>pabloo</span>
              </div>
              {/* Nav */}
              {NAV.map((item) => {
                const active = item === "Refunds";
                return (
                  <div key={item} className="relative">
                    {active && (
                      <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r"
                        style={{ background: "#f97316" }} />
                    )}
                    <div
                      className="mx-2 px-3 py-2 rounded text-xs cursor-default"
                      style={{
                        color: active ? "#111827" : "#9ca3af",
                        fontWeight: active ? 600 : 400,
                        background: active ? "#f9f5f1" : "transparent",
                      }}
                    >
                      {item}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Main content ─────────────────────────────────────────── */}
            <div className="flex-1 flex min-w-0 overflow-hidden">
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

                {/* Breadcrumb bar */}
                <div className="flex items-center gap-1.5 px-4 md:px-6 py-2 border-b"
                  style={{ background: "#fafaf8", borderColor: "#e8e6e2" }}>
                  <span className="text-xs" style={{ color: "#9ca3af" }}>Orders</span>
                  <span className="text-xs" style={{ color: "#d1d5db" }}>/</span>
                  <span className="text-xs" style={{ color: "#9ca3af" }}>Refund Requests</span>
                  <span className="text-xs" style={{ color: "#d1d5db" }}>/</span>
                  <span className="text-xs font-medium" style={{ color: "#374151" }}>#REF-2024-0892</span>
                  <div className="ml-auto">
                    {isSuccess ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded"
                        style={{ background: "#dcfce7", color: "#15803d" }}>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Credit Issued
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded"
                        style={{ background: "#fef3c7", color: "#b45309" }}>
                        Pending Your Approval
                      </span>
                    )}
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-3"
                  style={{ background: "#fafaf8" }}>

                  {/* ── Order summary card ──────────────────────────────── */}
                  <div
                    ref={orderCardRef}
                    className="bg-white rounded-lg border p-4"
                    style={{ borderColor: "#e8e6e2", ...focusStyle("orderCard") }}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <p className="text-xs mb-1" style={{ color: "#9ca3af" }}>Customer</p>
                        <p className="text-sm font-semibold" style={{ color: "#111827" }}>Sarah Johnson</p>
                        <p className="text-xs mt-0.5" style={{ color: "#6b7280" }}>sarah.j@gmail.com</p>
                      </div>
                      <div>
                        <p className="text-xs mb-1" style={{ color: "#9ca3af" }}>Item</p>
                        <p className="text-sm font-semibold" style={{ color: "#111827" }}>Linen Midi Dress</p>
                        <p className="text-xs mt-0.5" style={{ color: "#6b7280" }}>Sage, Size S · Qty 1</p>
                      </div>
                      <div>
                        <p className="text-xs mb-1" style={{ color: "#9ca3af" }}>Refund Amount</p>
                        <p className="text-xl font-bold" style={{ color: "#111827" }}>$85.00</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs mb-1" style={{ color: "#9ca3af" }}>Requested</p>
                        <p className="text-sm font-medium" style={{ color: "#374151" }}>Jun 10, 2026</p>
                        <p className="text-xs mt-0.5" style={{ color: "#9ca3af" }}>7 days ago</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t flex items-center gap-2"
                      style={{ borderColor: "#f3f2f0" }}>
                      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24" style={{ color: "#9ca3af" }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3v-3z" />
                      </svg>
                      <span className="text-xs" style={{ color: "#6b7280" }}>
                        Customer reason: <span style={{ color: "#374151" }}>Wrong size, too large</span>
                      </span>
                    </div>
                  </div>

                  {/* ── Pabloo AI Assessment ─────────────────────────────── */}
                  <div
                    ref={assessmentRef}
                    className="bg-white rounded-lg border overflow-hidden"
                    style={{ borderColor: "#e8e6e2", ...focusStyle("assessment") }}
                  >
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b"
                      style={{ background: "#f9f8f6", borderColor: "#e8e6e2" }}>
                      <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                        style={{ background: "#f97316" }}>
                        <span className="text-white font-bold" style={{ fontSize: 9 }}>P</span>
                      </div>
                      <span className="text-xs font-semibold tracking-wide uppercase"
                        style={{ color: "#374151" }}>
                        Pabloo AI · Assessment
                      </span>
                      <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded"
                        style={{ background: "#fff7ed", color: "#c2410c" }}>
                        Issue Store Credit
                      </span>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      {[
                        "Repeat customer: 4 orders, $412 spent in 90 days",
                        "Store credit redemption rate: 94% within 60 days",
                        "Avg. store credit basket is 2.3× the original order value",
                        "Monitor: no disputes on account. Safe to approve.",
                      ].map((text, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none"
                            stroke="currentColor" viewBox="0 0 24 24" style={{ color: "#16a34a" }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                              d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-xs" style={{ color: "#4b5563" }}>{text}</span>
                        </div>
                      ))}
                    </div>
                    {/* Metric bar */}
                    <div className="flex items-center gap-4 px-4 py-3 border-t"
                      style={{ borderColor: "#f3f2f0", background: "#fafaf8" }}>
                      {[
                        { label: "Customer Score", val: "96", max: 100, color: "#16a34a" },
                        { label: "Redemption Likelihood", val: "94/100", color: "#f97316" },
                        { label: "Disputes", val: "0", color: "#6b7280" },
                      ].map((m) => (
                        <div key={m.label} className="flex-1 min-w-0">
                          <p className="text-xs mb-1 truncate" style={{ color: "#9ca3af" }}>{m.label}</p>
                          <p className="text-sm font-bold" style={{ color: m.color }}>{m.val}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Refund breakdown ─────────────────────────────────── */}
                  <div
                    ref={breakdownRef}
                    className="bg-white rounded-lg border overflow-hidden"
                    style={{ borderColor: "#e8e6e2", ...focusStyle("breakdown") }}
                  >
                    <div className="px-4 py-2.5 border-b"
                      style={{ background: "#f9f8f6", borderColor: "#e8e6e2" }}>
                      <span className="text-xs font-semibold tracking-wide uppercase"
                        style={{ color: "#374151" }}>
                        Refund Options
                      </span>
                    </div>
                    <div className="flex divide-x" style={{ divideColor: "#e8e6e2" }}>
                      <div className="flex-1 px-4 py-3">
                        <p className="text-xs mb-1" style={{ color: "#9ca3af" }}>Cash Refund</p>
                        <p className="text-base font-bold" style={{ color: "#374151" }}>$85.00</p>
                        <p className="text-xs mt-1" style={{ color: "#ef4444" }}>Revenue lost permanently</p>
                      </div>
                      <div className="flex-1 px-4 py-3" style={{ background: "#fffbf7" }}>
                        <p className="text-xs mb-1" style={{ color: "#9ca3af" }}>Store Credit</p>
                        <div className="flex items-baseline gap-1.5">
                          <p className="text-base font-bold" style={{ color: "#111827" }}>$85.00</p>
                          <span className="text-xs font-medium px-1.5 py-0.5 rounded"
                            style={{ background: "#dcfce7", color: "#15803d" }}>
                            $0 fee
                          </span>
                        </div>
                        <p className="text-xs mt-1" style={{ color: "#16a34a" }}>Stays in your store</p>
                      </div>
                    </div>
                  </div>

                  {/* ── Action buttons ───────────────────────────────────── */}
                  {!isSuccess ? (
                    <div className="flex gap-2 pt-1">
                      <div
                        ref={buttonRef}
                        className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-center transition-all duration-150"
                        style={{
                          background: isHover
                            ? "#111827"
                            : isLoading
                            ? "#374151"
                            : "#1f2937",
                          color: "#ffffff",
                          transform: ui === "click" ? "scale(0.98)" : "scale(1)",
                          boxShadow: isHover
                            ? "0 0 0 3px rgba(249,115,22,0.35)"
                            : "0 1px 3px rgba(0,0,0,0.2)",
                        }}
                      >
                        {isLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10"
                                stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Issuing Store Credit…
                          </span>
                        ) : (
                          "Issue Store Credit"
                        )}
                      </div>
                      <div
                        className="px-6 py-2.5 rounded-lg text-sm font-medium text-center"
                        style={{
                          background: "#ffffff",
                          color: "#6b7280",
                          border: "1px solid #e8e6e2",
                        }}
                      >
                        Cash Refund
                      </div>
                    </div>
                  ) : (
                    <div
                      ref={successMsgRef}
                      className="rounded-lg px-4 py-3 flex items-start gap-3"
                      style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}
                    >
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: "#16a34a" }}>
                        <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: "#166534" }}>
                          $85.00 store credit issued to Sarah Johnson
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: "#16a34a" }}>
                          Email sent · Redeemable immediately at checkout
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ── Recent refunds mini-table ─────────────────────────── */}
                  <div className="bg-white rounded-lg border overflow-hidden"
                    style={{ borderColor: "#e8e6e2" }}>
                    <div className="px-4 py-2.5 border-b"
                      style={{ background: "#f9f8f6", borderColor: "#e8e6e2" }}>
                      <span className="text-xs font-semibold tracking-wide uppercase"
                        style={{ color: "#374151" }}>
                        Previous Refunds · Same Customer
                      </span>
                    </div>
                    {[
                      { date: "Mar 2024", item: "Wool Blazer, Navy",    amount: "$210.00", status: "Store Credit", statusColor: "#16a34a", statusBg: "#dcfce7" },
                      { date: "Jan 2024", item: "Cashmere Sweater",     amount: "$135.00", status: "Store Credit", statusColor: "#16a34a", statusBg: "#dcfce7" },
                    ].map((row, i) => (
                      <div key={i}
                        className="flex items-center gap-4 px-4 py-2.5 border-t flex-wrap"
                        style={{ borderColor: "#f3f2f0" }}>
                        <span className="text-xs w-16 flex-shrink-0" style={{ color: "#9ca3af" }}>{row.date}</span>
                        <span className="text-xs flex-1 font-medium" style={{ color: "#374151" }}>{row.item}</span>
                        <span className="text-xs font-semibold" style={{ color: "#111827" }}>{row.amount}</span>
                        <span className="text-xs font-medium px-2 py-0.5 rounded"
                          style={{ background: row.statusBg, color: row.statusColor }}>
                          {row.status}
                        </span>
                      </div>
                    ))}
                  </div>

                </div>{/* /body */}
              </div>{/* /main-col */}

              {/* ── AI panel (hidden on mobile) ──────────────────────────── */}
              <div
                ref={aiPanelRef}
                className="hidden lg:flex w-52 flex-shrink-0 flex-col border-l"
                style={{ background: "#ffffff", borderColor: "#e8e6e2" }}
              >
                <div className="flex items-center justify-between px-4 py-2.5 border-b"
                  style={{ background: "#f9f8f6", borderColor: "#e8e6e2" }}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded flex items-center justify-center"
                      style={{ background: "#f97316" }}>
                      <span className="text-white font-bold" style={{ fontSize: 9 }}>P</span>
                    </div>
                    <span className="text-xs font-semibold" style={{ color: "#111827" }}>Pabloo AI</span>
                  </div>
                  <span className="text-xs" style={{ color: "#9ca3af" }}>connected</span>
                </div>
                <div className="flex-1 flex flex-col gap-2 p-3 overflow-y-auto"
                  style={{ background: "#fafaf8" }}>
                  {/* AI message 1 */}
                  <div className="rounded-lg p-3 text-xs leading-relaxed"
                    style={{ background: "#ffffff", border: "1px solid #e8e6e2", color: "#374151" }}>
                    <p className="font-semibold mb-1" style={{ color: "#111827" }}>
                      #REF-2024-0892 ready for review.
                    </p>
                    Sarah has a strong track record: 4 purchases, zero disputes.
                  </div>
                  {/* AI message 2 */}
                  <div className="rounded-lg p-3 text-xs leading-relaxed"
                    style={{ background: "#ffffff", border: "1px solid #e8e6e2", color: "#374151" }}>
                    Store credit keeps <span className="font-semibold" style={{ color: "#111827" }}>$85</span> in your store. Her next order averages{" "}
                    <span className="font-semibold" style={{ color: "#f97316" }}>$110</span>.
                    <p className="mt-1.5" style={{ color: "#9ca3af" }}>Is it safe to approve?</p>
                  </div>
                  {/* AI response */}
                  <div
                    className="rounded-lg p-3 text-xs leading-relaxed transition-all duration-700"
                    style={{
                      background: isSuccess ? "#f0fdf4" : "#ffffff",
                      border: isSuccess ? "1px solid #bbf7d0" : "1px solid #e8e6e2",
                      color: isSuccess ? "#166534" : "#9ca3af",
                    }}
                  >
                    {isSuccess
                      ? "Store credit issued. Sarah can shop again today. No revenue lost."
                      : "Yes, clean history. Safe to approve with store credit."}
                  </div>
                </div>
              </div>

            </div>{/* /flex content+panel */}
          </div>{/* /app layout */}
        </div>{/* /demo shell */}

      </div>

      <style>{`
        @keyframes pbl-ripple {
          0%   { transform: scale(0.3); opacity: 1; }
          100% { transform: scale(2.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
